// Cover through (migration 061, shared/cover.ts; Mitch, 2026-11-03: "ensure we had no stockouts through December 31st …
// input a date globally for all purchasing plans, but then also select a date per-vendor which overrides the global
// one … saved per user"): the Purchasing plan's header holds the person's day for every vendor, each vendor's header its
// own. Typed as days or a date, with days added or taken away ("Dec 31 + 14"); either shows as both.
import { CalendarRange, X } from "lucide-react";
import { useState } from "react";
import { type Cover, coverLabel, coverOf, parseCover } from "../../../shared/cover.ts";
import type { PlanCover, Vendor } from "../../../shared/db.gen.ts";
import { planToday } from "../../../shared/planning.ts";
import { mutate } from "../lib/mutate.ts";
import { me } from "../lib/session.ts";
import { useRows } from "../lib/sync.ts";
import { Button, cx } from "../ui/controls.tsx";
import { Popover } from "../ui/Popover.tsx";

/** The signed-in person's row for every vendor (`vendorId` null) or one vendor (one just made shows before its owner). */
const mineFor = (rows: readonly PlanCover[], vendorId: string | null) => {
  const id = me()?.id;
  return rows.find((r) => (!r.user_id || r.user_id === id) && (r.vendor_id ?? null) === vendorId);
};

async function save(row: PlanCover | undefined, vendorId: string | null, c: Cover | null) {
  if (!c) return row ? mutate("plan_cover.remove", { id: row.id }) : true;
  const value = { days: c.days ?? null, through: c.through ?? null };
  return row
    ? mutate("plan_cover.update", { id: row.id, patch: value })
    : mutate("plan_cover.create", { id: crypto.randomUUID(), vendor_id: vendorId, ...value });
}

/** The button and its box: for every vendor (`vendor` null) or one. */
export function CoverThrough({ vendor, compact }: { vendor: Pick<Vendor, "id" | "name" | "short_name"> | null; compact?: boolean }) {
  const rows = useRows("plan_cover");
  const today = planToday();
  const row = mineFor(rows, vendor?.id ?? null);
  const own = coverOf(row);
  const all = vendor ? coverOf(mineFor(rows, null)) : null;
  const shown = own ?? all;
  const name = vendor ? vendor.short_name || vendor.name : "";
  const title = vendor
    ? `How far ahead you're buying from ${name} (yours alone): what would run short before then is due now, ordered to last to then`
    : "How far ahead you're buying from every vendor (yours alone; a vendor's own day wins): what would run short before then is due now, ordered to last to then";
  return (
    <Popover
      width={360}
      button={(toggle, open) =>
        compact ? (
          <button
            type="button"
            title={title}
            onClick={toggle}
            className={cx(
              "flex items-center gap-1 rounded px-1 py-0.5 text-[12px] whitespace-nowrap hover:bg-hover",
              own ? "text-accent-text" : shown ? "text-muted" : "text-faint",
              open && "bg-hover",
            )}
          >
            <CalendarRange className="size-3.5" />
            {own ? `To ${coverLabel(own, today)}` : all ? `To ${coverLabel(all, today)} (all)` : "Cover through…"}
          </button>
        ) : (
          <Button variant="ghost" icon={<CalendarRange className="size-4" />} title={title} onClick={toggle}>
            {own ? `Cover to ${coverLabel(own, today)}` : "Cover through"}
          </Button>
        )
      }
    >
      {(close) => <CoverBox key={row?.id ?? "new"} vendorName={name} row={row} own={own} all={all} close={close} vendorId={vendor?.id ?? null} />}
    </Popover>
  );
}

function CoverBox({
  vendorName,
  vendorId,
  row,
  own,
  all,
  close,
}: {
  vendorName: string;
  vendorId: string | null;
  row: PlanCover | undefined;
  own: Cover | null;
  all: Cover | null;
  close: () => void;
}) {
  const today = planToday();
  // What's set shows as typed: days as days, a date as the date.
  const [text, setText] = useState(own ? (own.days ? `${own.days}` : coverLabel(own, today).split(" · ")[0]) : "");
  const parsed = parseCover(text, today);
  const error = parsed && "error" in parsed ? parsed.error : "";
  const cover = parsed && !("error" in parsed) ? parsed : null;
  const apply = async (c: Cover | null) => {
    if (await save(row, vendorId, c)) close();
  };
  return (
    <form
      className="flex flex-col gap-2"
      onSubmit={(e) => {
        e.preventDefault();
        if (!error) void apply(cover);
      }}
    >
      <div className="font-medium">Cover through{vendorName ? ` · ${vendorName}` : " · every vendor"}</div>
      <input
        // biome-ignore lint/a11y/noAutofocus: the box opens to type in
        autoFocus
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder="90 days, Dec 31, Dec 31 + 14"
        aria-label="Cover through"
        className="h-8 rounded-md border border-line bg-surface px-2 outline-none focus:border-accent"
      />
      <div className={cx("min-h-4 text-[12px]", error ? "text-warn" : "text-muted")}>
        {error ||
          (cover
            ? `= ${coverLabel(cover, today)}`
            : vendorName && all
              ? `Empty: your day for every vendor (${coverLabel(all, today)})`
              : "Empty: the plan's usual")}
      </div>
      <p className="text-[12px] text-faint">
        What you buy{vendorName ? ` from ${vendorName}` : ""} that would fall below its safety stock before then is due now, and ordered to last to then at
        least. Days roll forward each day; a date stays put. {vendorName ? "Wins over your day for every vendor." : "A vendor's own day wins."} Only you see it.
      </p>
      <div className="flex items-center justify-end gap-2">
        {row && (
          <Button size="sm" variant="ghost" icon={<X className="size-3.5" />} onClick={() => void apply(null)}>
            Clear
          </Button>
        )}
        <Button size="sm" variant="primary" type="submit" disabled={!!error || (!cover && !row)}>
          Save
        </Button>
      </div>
    </form>
  );
}
